import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/use-user-role";
import { ThemeToggle } from "@/components/ThemeToggle";
import logo from "@/assets/logo.svg";

interface MarketingHeaderProps {
  user?: any;
}

export const MarketingHeader = ({ user }: MarketingHeaderProps) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isAdmin, isCoach } = useUserRole();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className={`border-b border-border/40 fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? "bg-background/95 backdrop-blur-md shadow-lg" 
        : "bg-background/80 backdrop-blur-sm"
    }`}
    >
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <div className="flex items-center gap-2 md:gap-3 cursor-pointer" onClick={() => navigate("/")}>
          <img src={logo} alt="Vocalzing logo" className="h-8 w-8 md:h-10 md:w-10" />
          <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Vocalzing
          </h1>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex gap-4 items-center">
          <ThemeToggle />
          {user ? (
            <>
              {isAdmin ? (
                <Button 
                  variant="secondary" 
                  onClick={() => navigate("/admin")}
                  className="flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  Admin Portal
                </Button>
              ) : isCoach ? (
                <Button 
                  variant="secondary" 
                  onClick={() => navigate("/coach")}
                  className="flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  Coach Portal
                </Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => navigate("/booking")}>Book Lesson</Button>
                  <Button variant="ghost" onClick={() => navigate("/progress")}>Progress</Button>
                </>
              )}
              <Button variant="outline" onClick={async () => {
                await supabase.auth.signOut();
                navigate("/auth");
              }}>Logout</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/about")}>About</Button>
              <Button variant="ghost" onClick={() => navigate("/pricing")}>Pricing</Button>
              <Button variant="ghost" onClick={() => navigate("/auth")}>Sign In</Button>
              <Button variant="hero" onClick={() => navigate("/auth")}>Get Started</Button>
              <Button variant="outline" onClick={() => navigate("/apply-as-coach")}>Become a Coach</Button>
            </>
          )}
        </div>

        {/* Mobile Hamburger Menu */}
        <div className="flex md:hidden items-center gap-2">
          <ThemeToggle />
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="sm">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px]">
            <div className="flex flex-col gap-4 mt-8">
              {user ? (
                <>
                  {isAdmin ? (
                    <Button 
                      variant="secondary" 
                      onClick={() => {
                        navigate("/admin");
                        setMobileMenuOpen(false);
                      }}
                      className="w-full justify-start"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Portal
                    </Button>
                  ) : isCoach ? (
                    <Button 
                      variant="secondary" 
                      onClick={() => {
                        navigate("/coach");
                        setMobileMenuOpen(false);
                      }}
                      className="w-full justify-start"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Coach Portal
                    </Button>
                  ) : (
                    <>
                      <Button 
                        variant="ghost" 
                        onClick={() => {
                          navigate("/booking");
                          setMobileMenuOpen(false);
                        }}
                        className="w-full justify-start"
                      >
                        Book Lesson
                      </Button>
                      <Button 
                        variant="ghost" 
                        onClick={() => {
                          navigate("/progress");
                          setMobileMenuOpen(false);
                        }}
                        className="w-full justify-start"
                      >
                        Progress
                      </Button>
                    </>
                  )}
                  <Button 
                    variant="outline" 
                    onClick={async () => {
                      await supabase.auth.signOut();
                      navigate("/auth");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      navigate("/about");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    About
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      navigate("/pricing");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    Pricing
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      navigate("/auth");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    Sign In
                  </Button>
                  <Button 
                    variant="hero" 
                    onClick={() => {
                      navigate("/auth");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full"
                  >
                    Get Started
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      navigate("/apply-as-coach");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    Become a Coach
                  </Button>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
        </div>
      </div>
    </nav>
  );
};
